import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import {
  CharterGovernanceDto,
  CharterHolderDto,
  CharterHoldersDisclosure,
  CharterLegalNature,
  CharterReceitaOnlyDto,
  CharterReceitaStatus,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { CharterHistoryListComponent } from '@features/dashboard/company/corporate-charter/components/charter-history-list/charter-history-list.component';
import { CharterProvenanceComponent } from '@features/dashboard/company/corporate-charter/components/charter-provenance/charter-provenance.component';
import {
  CharterProvenance,
  dominantSource,
  holderHistoryRows,
  isLaterThanBase,
} from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import {
  CharterPersonSigning,
  personSigning,
} from '@features/dashboard/company/corporate-charter/corporate-charter-signing.util';
import { CharterPersonDocumentComponent } from '@features/dashboard/company/corporate-charter/components/charter-person-document/charter-person-document.component';
import {
  formatMoney,
  formatPercent,
  formatUnits,
  isCorporation,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/** Ícone da coluna "Assina pela empresa". */
const SIGN_ICON: Record<CharterPersonSigning['tone'], string> = {
  sole: 'pi-pencil',
  joint: 'pi-users',
  approval: 'pi-sitemap',
  none: 'pi-minus',
  unknown: 'pi-question-circle',
};

const RECEITA_LABEL: Record<CharterReceitaStatus, string> = {
  match: 'consta',
  'missing-in-receita': 'ainda não consta',
  'not-checked': 'não conferido',
};

/**
 * Quadro de sócios (Ltda) ou acionistas (S.A.). Respeita `holdersStatus`: com `not-disclosed` mostra "Acionistas não
 * informados no documento" (o Cog nunca completa a lista); com `partial`, a lista de citados em atas, sem cobrar soma.
 * O documento de cada pessoa vem mascarado; o olho revela para quem tem permissão. Cada sócio mostra de onde vieram os
 * dados vigentes ("vigente desde <ato>") e, em **Ver histórico**, os valores de cada ato anterior com a procedência.
 */
@Component({
  selector: 'app-charter-holders-card',
  imports: [
    TooltipModule,
    VaultDatePipe,
    CharterPersonDocumentComponent,
    CharterProvenanceComponent,
    CharterHistoryListComponent,
  ],
  templateUrl: './charter-holders-card.component.html',
  styleUrl: './charter-holders-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterHoldersCardComponent {
  readonly holders = input<CharterHolderDto[]>([]);
  readonly holdersStatus = input<CharterHoldersDisclosure>('not-disclosed');
  readonly receitaOnly = input<CharterReceitaOnlyDto[]>([]);
  readonly unitKind = input<'quotas' | 'shares'>('quotas');
  readonly legalNature = input<CharterLegalNature | null>(null);
  readonly currency = input('BRL');
  /** Administração e regras de assinatura: alimenta a coluna "Assina pela empresa". */
  readonly governance = input<CharterGovernanceDto | null>(null);

  protected readonly signIcon = SIGN_ICON;

  protected readonly receitaLabel = RECEITA_LABEL;
  protected readonly formatPercent = formatPercent;
  protected readonly formatUnits = formatUnits;

  /** Sócios com o histórico aberto (por pessoa). */
  protected readonly expanded = signal<ReadonlySet<string>>(new Set());

  /** Fonte dominante do quadro: o ato de onde vem a maioria dos sócios vigentes. */
  protected readonly source = computed(() => dominantSource(this.holders().map(holderSource)));

  protected readonly rows = computed(() => {
    const base = this.source();
    const governance = this.governance();
    return this.holders().map(holder => {
      const source = holderSource(holder);
      return {
        holder,
        source,
        /** Os dados vigentes vêm de outro ato que não a fonte do quadro: mostra "vigente desde". */
        changed: isLaterThanBase(source, base),
        entityTag: entityTag(holder),
        signing: governance
          ? personSigning(
              holder.personId,
              holder.name,
              governance,
              this.isCorporation(),
              this.currency()
            )
          : fallbackSigning(holder),
        history: holderHistoryRows(holder.history ?? [], this.unitKind(), this.currency()),
      };
    });
  });

  protected readonly isCorporation = computed(
    () => isCorporation(this.legalNature()) || this.unitKind() === 'shares'
  );

  protected readonly title = computed(() =>
    this.isCorporation() ? 'Acionistas' : 'Quadro de sócios'
  );

  protected readonly holderNoun = computed(() => (this.isCorporation() ? 'Acionista' : 'Sócio'));

  protected readonly unitsHeader = computed(() =>
    this.unitKind() === 'shares' ? 'Ações' : 'Quotas'
  );

  protected readonly summary = computed(() => {
    const count = this.holders().length;
    if (this.holdersStatus() === 'partial') {
      return `${count} citado${count === 1 ? '' : 's'} em atas · lista parcial`;
    }
    const noun = this.isCorporation() ? 'acionista' : 'sócio';
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
  });

  /** Ações por classe da S.A.: "ON 30.000.000 · PN 20.000.000". */
  protected unitsByClass(holder: CharterHolderDto): string | null {
    if (holder.unitsByClass.length === 0) {
      return null;
    }
    return holder.unitsByClass
      .map(item => `${item.class.toUpperCase()} ${formatUnits(item.units)}`)
      .join(' · ');
  }

  protected toggleHistory(personId: string): void {
    this.expanded.update(set => {
      const next = new Set(set);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  }

  protected money(value: number | null): string {
    return formatMoney(value, this.currency());
  }
}

/** Sócio pessoa jurídica ou fundo: selo destacado no quadro. */
function entityTag(holder: CharterHolderDto): { label: string; icon: string; hint: string } | null {
  switch (holder.personKind) {
    case 'company':
      return {
        label: 'Holding',
        icon: 'pi-building',
        hint: 'Sócio pessoa jurídica: quem assina por ela é o representante indicado no ato',
      };
    case 'fund':
      return { label: 'Fundo', icon: 'pi-briefcase', hint: 'Sócio fundo de investimento' };
    default:
      return null;
  }
}

function fallbackSigning(holder: CharterHolderDto): CharterPersonSigning {
  return holder.isAdministrator
    ? { label: 'Administrador', tone: 'unknown', tooltip: null }
    : { label: 'Não assina', tone: 'none', tooltip: null };
}

/** Procedência do sócio no vigente; sem ela (resposta antiga), a do percentual ou das quotas. */
function holderSource(holder: CharterHolderDto): CharterProvenance {
  if (holder.provenance?.actLabel || holder.provenance?.documentId) {
    return holder.provenance;
  }
  return holder.percentage.actLabel ? holder.percentage : holder.units;
}
